<template>
  <div>
    <exo-form hideClose :root="_self" :clear="false">
      <template v-slot:titleTooltip>
        <exo-help-dialog
          :color="'black--text'"
          :margin="'m-0'"
          v-if="inputs.type === 'Deployment Scenario'"
          title="Deployment Scenario"
        >
          <b>General description</b>
          <br />
          This mission allows the user to compute performance metrics for the
          deployment of a constellation, starting from a launch sequence
          specified by the user. The mission costs and durations are thus
          evaluated for this sequence. More specifically, for each satellite, an
          analytical RAAN Phasing Mission is created and computed in order to
          evaluate the cost of deploying the constellation using propulsion.
        </exo-help-dialog>
        <exo-help-dialog
          :color="'white--text'"
          :margin="'m-0'"
          v-if="inputs.type === 'Deployment Optimization'"
          title="Deployment Optimization"
        >
          <b>General description</b>
          <br />
          <br />
          The Deployment Optimization mission optimises the deployment of a
          constellation, given a set of available launches.
          <br />
          <br />
          Every possible launch sequence (or launch sequence) is generated and
          computed automatically. For each launch sequence, spacecrafts that are
          not placed on the right orbit by their launcher perform a plane change
          (RAAN phasing) manoeuvre to reach their final position.
          <br />
          <br />
          Meaningful results are produced for each launch sequence, including
          the exploitation date of the constellation and the total cost of its
          deployment. Launch sequences can be sorted according to these metrics
          in order to find an optimal deployment strategy.
        </exo-help-dialog>
      </template>
      <exo-input-name :root="_self" name="name" />
      <v-row no-gutters>
        <v-col cols="6">
          <exo-card-form label="Constellation">
            <div class="mt-3" />
            <exo-input-date-time :root="_self" name="raanLtanReferenceDate">
              <exo-help-dialog margin="mt-2 ml-2" title="Reference date">
                This is the reference date for the constellation initial
                RAAN/MLTAN
              </exo-help-dialog>
            </exo-input-date-time>
            <exo-input-ref-notparametric
              :root="_self"
              name="constellation"
              type="constellation"
              componentRoute="/analyticalconstellationheterogeneous"
            />
            <exo-form-alert
              style="margin-left: -15px"
              class="mb-2"
              type="error"
              v-if="!displayRequirements"
            >
              {{ displayRequirementsErrorMessage }}
            </exo-form-alert>
            <exo-form-alert
              style="margin-left: -15px"
              class="mb-2"
              type="info"
              v-if="displayRequirements"
            >
              {{ totalSpacecraftsCount }} spacecrafts to deploy on
              {{ planesCount }} planes, each spacecraft weights
              {{ spacecraftTotalMass }} kg<br />
              Altitude : {{ constellationAltitude }} km , inclination :
              {{ constellationInclination }} deg<br />
            </exo-form-alert>
            <exo-input-double :root="_self" name="durationCost">
              <exo-help-dialog title="Cost functions">
                <tooltipCosts />
              </exo-help-dialog>
            </exo-input-double>
          </exo-card-form>
        </v-col>
        <v-col cols="6">
          <exo-card-form label="Plane change (RAAN phasing) manoeuvres">
            <exo-input-select :root="_self" name="deploymentOptimizationType">
              <exo-help-dialog title="Optimise on">
                Plane change manoeuvres can be performed with two optimisation
                scenarios:
                <ul>
                  <li>
                    minimising the manoeuvre duration, given a ΔVmax budget;
                  </li>
                  <li>minimising the ΔV, given a target manoeuvre duration.</li>
                </ul>
                This parameter is shared by all spacecrafts.
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-double :root="_self" name="targetDuration" />
            <exo-input-double :root="_self" name="targetDeltaVmax" />
            <exo-input-checkbox :root="_self" name="dragUsed">
              <exo-help-dialog
                title="Drag perturbation enabled"
                margin="mb-6 ml-2"
              >
                It is possible to consider the atmospheric drag perturbation
                during plane change manoeuvres. To this end, the user must
                ensure that the spacecrafts of the deployed constellation define
                a drag model and that their propulsion system is electrical.
                <br />
                <br />
                This parameter is shared by all spacecrafts.
              </exo-help-dialog>
            </exo-input-checkbox>
          </exo-card-form>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12">
          <exo-card-form v-if="displayPlanning" label="Launch planning">
            <v-data-table
              :headers="planningTableHeaders"
              :items="planningTableItems"
              :footer-props="{ itemsPerPageOptions: rowsPerPageItems }"
              :options.sync="pagination"
              style="overflow: auto"
            >
              <template
                v-for="h in planningTableHeaders"
                v-slot:[`header.${h.value}`]
              >
                <div :key="h.value" v-if="h.value !== 'firstColumn'">
                  <div slot="activator"></div>
                  <div>
                    <span>{{ h.text + getRaanOrLtan(h.value - 1) }}</span>
                  </div>
                  <div>
                    <span
                      v-bind:style="{
                        color:
                          currentConstellation.numberOfSpacecraftsPerPlane -
                            spacecraftPerPlane(h.value - 1) ===
                          0
                            ? 'green'
                            : 'orange'
                      }"
                    >
                      Spacecrafts : {{ spacecraftPerPlane(h.value - 1) }} /
                      {{ currentConstellation.numberOfSpacecraftsPerPlane }}
                    </span>
                  </div>
                </div>
              </template>
              <template slot="item" slot-scope="row">
                <tr>
                  <td
                    class="px-1 text-right"
                    v-for="(header, headerIndex) in planningTableHeaders"
                    :key="headerIndex"
                  >
                    <div>
                      <div
                        style="min-width: 150px"
                        class="text-center ml-2 mr-5"
                        v-if="headerIndex === 0"
                      >
                        <v-row no-gutters align="center">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-col cols="8" v-on="on">
                                <exo-input-ref-notparametric
                                  :root="_self"
                                  name="launch"
                                  type="launch"
                                  componentRoute="/deploymentlaunch"
                                  :parentObject="row.item"
                                />
                              </v-col>
                            </template>
                            <span
                              style="
                                margin: 0;
                                padding: 0;
                                white-space: pre-line;
                              "
                              >{{ _self.getInfos(row.item.launch) }}</span
                            >
                          </v-tooltip>
                          <v-spacer />
                          <v-col cols="2">
                            <v-btn
                              icon
                              dark
                              class="ma-0"
                              @click="deleteBooking(row.index)"
                            >
                              <v-icon color="black">delete</v-icon>
                            </v-btn>
                          </v-col>
                          <v-col>
                            <v-chip
                              class="ma-0"
                              color="info"
                              label
                              text-color="white"
                            >
                              <v-icon left>mdi-label</v-icon>
                              {{ spacecraftPerLaunch(row) }}
                            </v-chip>
                          </v-col>
                        </v-row>
                      </div>
                      <div
                        class="text-left grey--text ml-1 mr-5"
                        v-if="headerIndex === 0"
                      >
                        {{
                          row == null ||
                          row.item == null ||
                          row.item.launch == null ||
                          row.item.launch.name == null ||
                          launchRaansAtRef == null ||
                          launchRaansAtRef[row.item.launch.name] == null ||
                          launchRaansAtRef[row.item.launch.name].label == null
                            ? ''
                            : launchRaansAtRef[row.item.launch.name].label
                        }}
                      </div>
                      <v-row no-gutters>
                        <v-col
                          v-if="headerIndex >= firstPlaneIndex"
                          class="mt-2 mb-1"
                        >
                          <v-text-field
                            horizontal
                            v-model="
                              row.item.numberOfSpacecraftsPerPlanes[
                                headerIndex - firstPlaneIndex
                              ]
                            "
                            type="number"
                            flat
                            filled
                            :rules="[rules.validSpacecraftCount]"
                          >
                          </v-text-field>
                        </v-col>
                      </v-row>
                    </div>
                  </td>
                </tr>
              </template>
            </v-data-table>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  :disabled="remainingSpacecraftsCount <= 0"
                  text
                  small
                  outlined
                  color="blue lighten-1"
                  v-on:click="addBooking"
                  v-on="on"
                >
                  <v-icon>add</v-icon>
                  Book launch
                </v-btn>
              </template>
              <span>Book a launch to deploy some spacecrafts</span>
            </v-tooltip>
            <exo-form-alert
              :type="remainingSpacecraftsCount !== 0 ? 'error' : 'success'"
              v-if="displayRequirements"
            >
              {{ remainingSpacecraftsCount }} remaining spacecrafts to deploy
            </exo-form-alert>
          </exo-card-form>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="6">
          <exo-card-form
            label="Launch sequence generation"
            v-if="displayLaunchConfiguration"
          >
            <exo-input-ref-multiple
              :root="_self"
              name="launches"
              :isParametric="false"
              type="launch"
              componentRoute="/deploymentlaunch"
            >
              <exo-help-dialog title="Available launches">
                Available launches must be selected by the user in order to
                deploy a constellation. Launch sequences generated by the
                Deployment Optimization mission will not necessarily use all
                available launches.
                <br />
                <br />
                Be carefull to select enough launches in terms of payload
                capacity.
              </exo-help-dialog>
            </exo-input-ref-multiple>
          </exo-card-form>
        </v-col>
        <v-col cols="6" v-if="isRequiredOrOptional('resultSorting', inputs)">
          <exo-card-form
            label="Results sorting"
            v-if="displayLaunchConfiguration"
          >
            <template v-slot:titleTooltip>
              <exo-help-dialog margin="ml-2 mt-0" title="Results sorting">
                A thousand launch sequences can be displayed at most.
              </exo-help-dialog>
            </template>
            <exo-input-select :root="_self" name="resultSorting" />
            <exo-input-select :root="_self" name="sortingDirection" />
          </exo-card-form>
        </v-col>
      </v-row>
      <!-- filtersAndGroups is not a real field, it is used to activate other fields with the advanced settings
          mechanism. It is defined as optional to be used in isRequiredOrOptional, and not displayed in the form. -->
      <v-row no-gutters v-if="isRequiredOrOptional('filtersAndGroups', inputs)">
        <v-col cols="6">
          <exo-card-form
            label="Results filtering"
            v-if="displayLaunchConfiguration"
          >
            <template v-slot:titleTooltip>
              <exo-help-dialog margin="ml-2 mt-0" title="Filters">
                Filters eliminate launch sequences that do not match specific
                requirements, in order to reduce the number of launch sequences
                displayed in the deployment optimization results.
              </exo-help-dialog>
            </template>
            <exo-input-select
              :multiple="true"
              :root="_self"
              name="expectedResultFilter"
            />
            <div
              v-if="
                inputs.expectedResultFilter.includes('SPACECRAFT_PER_LAUNCH')
              "
            >
              <exo-input-checkbox :root="_self" name="bookAllLaunches">
                <exo-help-dialog title="Book all launches" margin="ml-2 mt-0">
                  When this option is checked, all launches are booked for each
                  scenario. When this option is not checked, some scenario might
                  not use all launches. For each booked launch, the minimum
                  number of spacecrafts per launch will be respected.
                  <br />
                  <br />
                </exo-help-dialog>
              </exo-input-checkbox>
              <v-row no-gutters>
                <v-col cols="6">
                  <exo-input-double :root="_self" name="minSpacecraftPerLaunch">
                  </exo-input-double>
                </v-col>
                <v-col cols="6">
                  <exo-input-double :root="_self" name="maxSpacecraftPerLaunch">
                  </exo-input-double>
                </v-col>
              </v-row>
            </div>
            <v-row
              no-gutters
              v-if="inputs.expectedResultFilter.includes('LAUNCH_NUMBER')"
            >
              <v-col cols="6">
                <exo-input-double :root="_self" name="minLaunchNumber">
                </exo-input-double>
              </v-col>
              <v-col cols="6">
                <exo-input-double :root="_self" name="maxLaunchNumber">
                </exo-input-double>
              </v-col>
            </v-row>
            <v-row
              no-gutters
              v-if="inputs.expectedResultFilter.includes('TOTAL_COST')"
            >
              <v-col cols="6">
                <exo-input-double :root="_self" name="minTotalCost">
                </exo-input-double>
              </v-col>
              <v-col cols="6">
                <exo-input-double :root="_self" name="maxTotalCost">
                </exo-input-double>
              </v-col>
            </v-row>
            <v-row
              no-gutters
              v-if="inputs.expectedResultFilter.includes('TOTAL_DELTA_RAAN')"
            >
              <v-col cols="6">
                <exo-input-double :root="_self" name="minTotalDeltaRAAN">
                </exo-input-double>
              </v-col>
              <v-col cols="6">
                <exo-input-double :root="_self" name="maxTotalDeltaRAAN">
                </exo-input-double>
              </v-col>
            </v-row>
            <v-row
              no-gutters
              v-if="inputs.expectedResultFilter.includes('EXPLOITATION_DATE')"
            >
              <v-col cols="5">
                <exo-input-date :root="_self" name="minExploitationDate">
                </exo-input-date>
              </v-col>
              <v-col offset="1" cols="5">
                <exo-input-date :root="_self" name="maxExploitationDate">
                </exo-input-date>
              </v-col>
            </v-row>
            <exo-form-alert
              class="mb-2"
              type="error"
              v-if="
                this.inputs.minExploitationDate >
                this.inputs.maxExploitationDate
              "
            >
              Error: the min exploitation date must be earlier than the max
              exploitation date.
            </exo-form-alert>
          </exo-card-form>
        </v-col>
        <v-col cols="6" v-if="isRequiredOrOptional('resultSorting', inputs)">
          <exo-card-form
            label="Results grouping"
            v-if="displayLaunchConfiguration"
          >
            <template v-slot:titleTooltip>
              <exo-help-dialog margin="ml-2 mt-0" title="Groups">
                Groups gather similar launch sequences based on specific
                criteria. Only one sequence of a given group is computed in the
                deployment optimization process.
                <br />
                A <b>group key</b> is generated for each group. It is given in
                the detailed results view after each optimisation. All sequences
                gathered inside a same group can be computed individually thanks
                to this key (use <b>Study a group in detail</b> option).
              </exo-help-dialog>
            </template>
            <exo-input-select :root="_self" name="groupProcessingType">
            </exo-input-select>
            <exo-input-select
              :multiple="true"
              :root="_self"
              name="groupingCriteria"
            >
              <exo-help-dialog title="Grouping criteria" margin="mt-6">
                Different properties of launch sequences can be used to regroup
                similar ones:
                <ul>
                  <li><b>Same rideshare launches</b></li>
                  <li><b>Same dedicated launches</b></li>
                  <li>
                    <b>Number of dedicated launches</b> If "Same dedicated
                    launches" is selected, this will not have any impact on the
                    group creation.
                  </li>
                  <li>
                    <b>Launch phase duration</b> Sequences with the same
                    duration between the first and the last launch dates.
                  </li>
                  <li><b>Last launch date</b></li>
                  <li><b>Same dedicated launches without permutations</b></li>
                  <li>
                    <b>Total number of spacecraft in rideshare launches</b>
                  </li>
                </ul>
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-string :root="_self" name="groupKey">
              <exo-help-dialog title="Group key">
                A <b>group key</b> is generated for each group. It is given in
                the detailed results view after each optimisation. All sequences
                gathered inside a same group can be computed individually thanks
                to this key (use <b>Study a group in detail</b> option).
              </exo-help-dialog>
            </exo-input-string>
          </exo-card-form>
        </v-col>
      </v-row>
    </exo-form>
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import formAnalyticalMixin from '../../mixins/analyticalFoms.js'
import dataTools from '../../tools/data.js'
import missionTools from '../../store/mission.js'
import fieldsDescription from '../../tools/fieldsDescription.json'
import dataDescription from '../../tools/dataDescription.json'
import tooltipCosts from '../../components/Tooltips/tooltipCosts'
import { mapGetters, mapActions } from 'vuex'
import Vue from 'vue'

export default {
  name: 'DeploymentRequest',

  mixins: [formMixin, formAnalyticalMixin],

  components: {
    tooltipCosts
  },

  async mounted() {
    this.$root.$on(
      'request-computation-deployment',
      this.requestComputationDeploymentHandler
    )
    this.$root.$on('copy-mission', this.copyMissionHandler)
    this.$root.$on('create-mission', this.createMissionHandler)
    this.$root.$on('current-mission-updated', this.currentMissionUpdatedHandler)
    await this.refreshConstellations()
    await this.refreshLaunches()
    this.refreshForm()
    await this.refreshLaunches()
  },

  beforeDestroy() {
    this.$root.$off(
      'request-computation-deployment',
      this.requestComputationDeploymentHandler
    )
    this.$root.$off('copy-mission', this.copyMissionHandler)
    this.$root.$off('create-mission', this.createMissionHandler)
    this.$root.$off(
      'current-mission-updated',
      this.currentMissionUpdatedHandler
    )
  },

  computed: {
    ...mapGetters(['currentMission']),

    displayRequirementsErrorMessage() {
      if (!this.currentMission) {
        return 'Outdated data, please refresh your page our logout and login after'
      }
      if (!this.inputs.constellation) {
        return 'Please select a constellation to deploy'
      }
      if (!this.currentConstellation) {
        return 'Invalid constellation selected, no homogeneous geometry inside.'
      }
      if (!this.currentConstellation.spacecraft) {
        return 'Please define a spacecraft model into the constellation'
      }
      if (!this.currentConstellation.spacecraft.thruster) {
        return "Please define a propulsion system into the constellation's spacecraft model"
      }
      return ''
    },

    displayRequirements() {
      return this.displayRequirementsErrorMessage === ''
    },

    displayPlanning() {
      return (
        this.displayRequirements &&
        this.currentMission.type === 'Deployment Scenario'
      )
    },

    displayLaunchConfiguration() {
      return (
        this.displayRequirements &&
        this.currentMission.type !== 'Deployment Scenario'
      )
    },

    totalSpacecraftsCount() {
      return this.numberOfSpacecraftsPerPlane * this.planesCount
    },

    numberOfSpacecraftsPerPlane() {
      if (this.currentConstellation == null) {
        return 0
      }
      return this.currentConstellation.numberOfSpacecraftsPerPlane
    },

    planesCount() {
      if (this.currentConstellation == null) {
        return 0
      }
      return this.currentConstellation.numberOfPlanes
    },

    currentConstellation() {
      if (
        this.inputs.constellation?.homogeneousConstellationGeometries == null
      ) {
        return null
      }
      return this.inputs.constellation.homogeneousConstellationGeometries[0]
    },

    constellationAltitude() {
      return dataTools.displayNdigits(
        dataTools.m2km(this.currentConstellation.altitude),
        2
      )
    },
    constellationInclination() {
      return dataTools.displayNdigits(
        dataTools.rad2deg(this.currentConstellation.inclination),
        2
      )
    },
    spacecraftTotalMass() {
      return (
        this.currentConstellation.spacecraft.platformMass +
        this.currentConstellation.spacecraft.thruster.totalMass
      )
    },

    spacecraftInLaunchCount() {
      if (this.inputs.bookings == null) {
        return 0
      } else {
        let count = 0
        for (let booking of this.inputs.bookings) {
          if (booking.numberOfSpacecraftsPerPlanes != null) {
            for (let nbSpacecraft of booking.numberOfSpacecraftsPerPlanes) {
              const countInLaunch = parseInt(nbSpacecraft, 10)
              if (!isNaN(countInLaunch)) {
                count += countInLaunch
              }
            }
          }
        }
        return count
      }
    },

    remainingSpacecraftsCount() {
      return this.totalSpacecraftsCount - this.spacecraftInLaunchCount
    },

    isRaanOrLtan() {
      if (!this.currentConstellation) {
        return null
      }
      let isRaan = this.currentConstellation.deltaRaan !== 'NaN'
      if (isRaan) {
        return 'raan'
      } else {
        return 'ltan'
      }
    },

    planningTableHeaders() {
      let headers = []
      headers.push({ text: '', value: 'firstColumn', sortable: false })
      for (let i = 1; i <= this.planesCount; i++) {
        headers.push({ text: 'Plane ' + i, value: i, sortable: false })
      }
      return headers
    },

    planningTableItems() {
      let items = []
      if (this.inputs.bookings != null) {
        for (let booking of this.inputs.bookings) {
          items.push(booking)
        }
      }
      return items
    }
  },

  asyncComputed: {
    async launchRaansAtRef() {
      let map = {}
      if (
        this.inputs.raanLtanReferenceDate != null &&
        this.inputs.raanLtanReferenceDate !== ''
      ) {
        for (let launchItem of this.launchsNotParametrics) {
          map[launchItem.value.name] = await this.computeRaanOrLtanAtRef(
            launchItem.value,
            this.inputs.raanLtanReferenceDate,
            this.isRaanOrLtan
          )
        }
      }
      return map
    }
  },

  data() {
    return {
      tableFields: [],
      rowsPerPageItems: [20, 50, { text: 'All', value: -1 }],
      pagination: {
        rowsPerPage: -1
      },

      enumPropertiesList: this.getEnumProperties(),

      modalRef: { content: [] },
      showModal: false,

      constellationsNotParametrics: [],
      launchsNotParametrics: [],
      rules: {
        validSpacecraftCount: (value) =>
          (Number.isInteger(Number(value)) && value >= 0) ||
          'Positive number expected'
      },
      firstPlaneIndex: 1,

      inputs: {
        name: this.getDefaultValue('name'),
        raanLtanReferenceDate: this.getDefaultValue('raanLtanReferenceDate'),
        description: this.getDefaultValue('description'),
        type: this.getDefaultValue('type'),
        dragUsed: this.getDefaultValue('dragUsed'),
        constellation: null,
        deploymentOptimizationType: this.getDefaultValue(
          'deploymentOptimizationType'
        ),
        launches: [],
        bookings: [],
        targetDuration: this.getDefaultValue('targetDuration'),
        targetDeltaVmax: this.getDefaultValue('targetDeltaVmax'),

        groupProcessingType: this.getDefaultValue('groupProcessingType'),
        groupingCriteria: this.getDefaultValue('groupingCriteria'),
        groupKey: this.getDefaultValue('groupKey'),
        expectedResultFilter: this.getDefaultValue('expectedResultFilter'),
        resultSorting: this.getDefaultValue('resultSorting'),
        sortingDirection: this.getDefaultValue('sortingDirection'),
        durationCost: this.getDefaultValue('durationCost'),

        maxSpacecraftPerLaunch: this.getDefaultValue('maxSpacecraftPerLaunch'),
        minSpacecraftPerLaunch: this.getDefaultValue('minSpacecraftPerLaunch'),
        bookAllLaunches: this.getDefaultValue('bookAllLaunches'),
        minTotalDeltaRAAN: this.getDefaultValue('minTotalDeltaRAAN'),
        maxTotalDeltaRAAN: this.getDefaultValue('maxTotalDeltaRAAN'),
        minExploitationDate: this.getDefaultValue('minExploitationDate'),
        maxExploitationDate: this.getDefaultValue('maxExploitationDate'),
        minTotalCost: this.getDefaultValue('minTotalCost'),
        maxTotalCost: this.getDefaultValue('maxTotalCost'),
        maxLaunchNumber: this.getDefaultValue('maxLaunchNumber'),
        minLaunchNumber: this.getDefaultValue('minLaunchNumber'),
        layout: this.getDefaultValue('layout') // to save the dashboard, should be optional in fieldsdesscription to be automatically cpoied
      }
    }
  },

  watch: {
    'inputs.constellation': {
      deep: true,
      handler(constellation) {
        if (constellation != null && this.inputs.bookings != null) {
          if (
            constellation?.homogeneousConstellationGeometries == null ||
            constellation.homogeneousConstellationGeometries[0]
              ?.numberOfPlanes == null
          ) {
            this.inputs.bookings = []
            return
          }
          let newConstellationNumberOfPlans =
            constellation.homogeneousConstellationGeometries[0].numberOfPlanes
          for (let i = 0; i < this.inputs.bookings.length; i++) {
            if (this.inputs.bookings[i].numberOfSpacecraftsPerPlanes == null) {
              this.inputs.bookings[i].numberOfSpacecraftsPerPlanes = []
            }
            this.inputs.bookings[i].numberOfSpacecraftsPerPlanes =
              this.inputs.bookings[i].numberOfSpacecraftsPerPlanes.slice(
                0,
                newConstellationNumberOfPlans
              )
            for (let j = 0; j < newConstellationNumberOfPlans; j++) {
              if (!this.inputs.bookings[i].numberOfSpacecraftsPerPlanes[j]) {
                this.inputs.bookings[i].numberOfSpacecraftsPerPlanes[j] = 0
              }
            }
          }
        }
      }
    }
  },

  methods: {
    ...mapActions(['setCurrentMission']),

    requestComputationDeploymentHandler() {
      this.onSave()
    },
    copyMissionHandler(mission) {
      this.copyMission(mission)
    },
    createMissionHandler(mission) {
      this.createMission(mission)
    },
    currentMissionUpdatedHandler() {
      this.refreshForm()
    },

    refreshTable() {},

    spacecraftPerPlane(planeIndex) {
      let bookings = this.inputs.bookings
      if (!bookings || bookings.length === 0) {
        return 0
      }
      return bookings.reduce((acc, curr) => {
        if (
          !curr.numberOfSpacecraftsPerPlanes ||
          !curr.numberOfSpacecraftsPerPlanes[planeIndex]
        ) {
          return acc
        }
        return acc + parseInt(curr.numberOfSpacecraftsPerPlanes[planeIndex])
      }, 0)
    },

    getRaanOrLtan(planIndex) {
      let value
      if (this.isRaanOrLtan === 'raan') {
        value =
          planIndex * this.currentConstellation.deltaRaan +
          this.currentConstellation.firstPlaneInitialRaan
        value =
          dataTools.displayNdigits(dataTools.rad2deg(value) % 360, 2) + 'deg'
      } else {
        value =
          planIndex * this.currentConstellation.deltaLtan +
          this.currentConstellation.firstPlaneInitialLtan
        value =
          dataTools.displayNdigits(dataTools.sec2hour(value) % 24, 2) + 'h'
      }
      return ` (${value})`
    },

    spacecraftPerLaunch(row) {
      if (
        !row.item.numberOfSpacecraftsPerPlanes ||
        row.item.numberOfSpacecraftsPerPlanes.length === 0
      ) {
        return 0
      }
      return row.item.numberOfSpacecraftsPerPlanes.reduce(
        (acc, curr) => acc + parseInt(curr),
        0
      )
    },

    copyMission(mission) {
      if (mission && mission.typeName === this.getCurrentFormType()) {
        this.copy(mission)
        this.sendFormToServer()
      }
    },

    refreshForm() {
      if (
        this.currentMission != null &&
        this.currentMission.typeName === this.getCurrentFormType()
      ) {
        if (this.currentMission.id !== this.inputs.id) {
          // different mission reload completely the form
          this.edit(this.currentMission, 0)
        } else {
          // reloading the current mission
          // only refresh the updated fields
          const fieldsToUpdate = [
            this.constellationsNotParametrics,
            this.launchsNotParametrics
          ]
          missionTools.updateAllReferences(this, fieldsToUpdate)
          const currentBookings = this.inputs.bookings
          if (currentBookings != null) {
            for (let index = 0; index < currentBookings.length; index++) {
              //update launches
              for (
                let launch = 0;
                launch < this.launchsNotParametrics.length;
                launch++
              ) {
                const launchValue = this.launchsNotParametrics[launch].value
                for (let field in launchValue) {
                  if (
                    currentBookings[index].launch != null &&
                    launchValue.id === currentBookings[index].launch.id
                  ) {
                    this.inputs.bookings[index].launch[field] =
                      launchValue[field]
                  }
                }
              }
            }
          }
        }
      }
    },

    async refreshConstellations() {
      let self = this
      await this.$http
        .get(this.getAPI(`constellationdefinitions`))
        .then((response) => {
          self.constellationsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of constellation. ',
            e,
            'notification',
            'error'
          )
        })
    },

    onSave: function (evt) {
      this.inputs.saving = true
      this.$http
        .put(
          this.getCurrentAPI() + '/' + this.inputs.id,
          this.createObjectFromForm()
        )
        .then((response) => {
          this.inputs.saving = false
          this.$root.$emit('update-mission-list')
          this.$root.$emit('set-current-mission', response.data) // update fields and current mission
          // redirect to dashboard
          this.$router.push({ path: '/deploymentdashboard' })
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'notification',
            'error'
          )
        })
    },

    async refreshLaunches() {
      let self = this
      await this.$http
        .get(this.getAPI(`launchdefinitions`))
        .then((response) => {
          self.launchsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of launches. ',
            e,
            'notification',
            'error'
          )
        })
    },

    getRefList(param) {
      if (param === 'constellation') {
        return this.constellationsNotParametrics
      }
      if (param === 'launch') {
        return this.launchsNotParametrics
      }
    },

    getCurrentFormTitle() {
      return this.inputs.type
    },

    getCurrentFormType() {
      return 'DeploymentRequest'
    },

    createMission(mission) {
      if (mission.typeName === this.getCurrentFormType()) {
        this.clearForm()
        this.inputs.type = mission.type
        this.sendFormToServer()
      }
    },

    sendFormToServer() {
      this.inputs.saving = true
      this.$http
        .post(this.getCurrentAPI(), this.createObjectFromForm())
        .then((response) => {
          this.inputs.saving = false
          this.$root.$emit('set-current-mission', response.data)
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'DeploymentMissionErrorDisplay',
            'error'
          )
        })
    },

    findUniqueReferences(field, parentObject) {
      if (field === 'launches' && parentObject[field] != null) {
        let toReturn = []
        for (let oneItem of parentObject[field]) {
          let refInList = this.launchsNotParametrics.find(
            (el) => el != null && el.value != null && el.value.id === oneItem.id
          )
          if (refInList) {
            if (!toReturn.includes(refInList.value)) {
              toReturn.push(refInList.value)
            }
          } // bad ref otherwise
        }
        return toReturn
      } else {
        return parentObject[field]
      }
    },

    addFieldsListToBaseObject(
      baseObject,
      fieldsList,
      parentObject = this.inputs
    ) {
      for (let field of fieldsList) {
        if (this.getPropertiesDescription()[field] != null) {
          let fieldType = this.getPropertiesDescription()[field].type
          if (
            fieldType === 'enum' ||
            fieldType === 'boolean' ||
            fieldType === 'date'
          ) {
            baseObject[field] = parentObject[field]
          } else if (fieldsDescription[fieldType] != null) {
            baseObject[field] = this.findUniqueReferences(field, parentObject)
          } else {
            baseObject[field] = dataTools.convertGui2si(
              this.getFieldType(field),
              parentObject[field]
            )
          }
        }
      }
      return baseObject
    },

    clearLocalFields() {
      for (let field in this.getPropertiesDescription()) {
        if (this.inputs.hasOwnProperty(field)) {
          this.inputs[field] = this.getDefaultValue(field)
          let fieldType = this.getPropertiesDescription()[field].type
          if (dataDescription[fieldType] != null) {
            this.checkParametricValue(this.inputs, field)
          } else if (fieldsDescription[fieldType] != null) {
            this.checkParametricReference(this.inputs, field)
          }
        }
      }
      this.inputs.subMissions = []
    },

    copyLocalFields(item) {
      this.inputs.id = item.id
      this.copyFieldsToBaseObject(item, this.inputs)
    },

    addLocalFieldsFromForm(baseObject, fieldsList) {
      baseObject = this.addFieldsListToBaseObject(baseObject, fieldsList)
      baseObject.computationRequested = true
      return baseObject
    },

    copyFieldsToBaseObject(item, baseObject) {
      for (let field in this.getPropertiesDescription()) {
        if (item.hasOwnProperty(field)) {
          let fieldType = this.getPropertiesDescription()[field].type
          if (dataDescription[fieldType] !== undefined) {
            // parametric value
            baseObject[field] = dataTools.convertSi2gui(
              this.getFieldType(field),
              item[field]
            )
            this.checkParametricValue(baseObject, field)
          } else {
            baseObject[field] = item[field]
            if (fieldsDescription[fieldType] != null) {
              this.checkParametricReference(baseObject, field)
            }
          }
        }
      }
    },

    checkFilterFieldNotRequired(fieldName, filterName, filterRelatedFields) {
      // if true, don't need to check the state of this field
      return (
        filterRelatedFields.includes(fieldName) &&
        !this.inputs.expectedResultFilter.includes(filterName)
      )
    },

    getFieldState(parentObject, fieldName, required) {
      // for fields that come with expectedResultFilter
      let checkNotRequired = false
      checkNotRequired =
        checkNotRequired ||
        this.checkFilterFieldNotRequired(fieldName, 'SPACECRAFT_PER_LAUNCH', [
          'bookAllLaunches',
          'minSpacecraftPerLaunch',
          'maxSpacecraftPerLaunch'
        ])
      checkNotRequired =
        checkNotRequired ||
        this.checkFilterFieldNotRequired(fieldName, 'LAUNCH_NUMBER', [
          'minLaunchNumber',
          'maxLaunchNumber'
        ])
      checkNotRequired =
        checkNotRequired ||
        this.checkFilterFieldNotRequired(fieldName, 'TOTAL_COST', [
          'minTotalCost',
          'maxTotalCost'
        ])
      checkNotRequired =
        checkNotRequired ||
        this.checkFilterFieldNotRequired(fieldName, 'TOTAL_DELTA_RAAN', [
          'minTotalDeltaRAAN',
          'maxTotalDeltaRAAN'
        ])
      checkNotRequired =
        checkNotRequired ||
        this.checkFilterFieldNotRequired(fieldName, 'EXPLOITATION_DATE', [
          'minExploitationDate',
          'maxExploitationDate'
        ])
      if (checkNotRequired === true) {
        return { value: true, message: true }
      }

      let fieldState = formAnalyticalMixin.methods.getFieldState.call(
        this,
        parentObject,
        fieldName,
        required
      )

      return fieldState
    },

    /// ///// This code is almost copied between missions, orbits, and many other objects. Refactoring required ///////////
    getRequiredPropertiesList(instance) {
      if (Object.keys(instance).includes('numberOfSpacecraftsPerPlanes')) {
        // TODO workaround to be improved
        return dataTools.getPropertiesList(
          fieldsDescription['LaunchBookingDefinition'],
          null,
          'required'
        )
      }
      let missionType = this.getMissionType(instance)
      let objectType = this.getCurrentFormType()
      let requiredProperties = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        missionType,
        'required'
      )
      // add script inputs  (to be displayed in form)
      if (this.inputs.scriptConfig != null) {
        for (let field in this.inputs.scriptConfig.scriptInputs) {
          requiredProperties.push(field)
        }
      }
      return requiredProperties
    },

    getOptionalPropertiesList(instance) {
      let missionType = this.getMissionType(instance)
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        missionType,
        'optional'
      )
    },

    getAllPropertiesUnionList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      let optionalList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
      return [...new Set(requiredList.concat(optionalList))]
    },

    getMissionType(mission) {
      let types = {
        type: mission.type,
        deploymentOptimizationType: mission.deploymentOptimizationType
      }
      if (mission.type === 'Deployment Optimization') {
        types.groupProcessingType = mission.groupProcessingType
      }
      return types
    },

    async computeRaanOrLtanAtRef(launch, targetDate, ltanOrRaan) {
      let newRaan = {
        raan: NaN,
        label: ''
      }
      if (
        launch != null &&
        !isNaN(launch.raanAtLaunch) &&
        targetDate !== '' &&
        ltanOrRaan !== null
      ) {
        const raanCalculator = {
          semiMajorAxis: launch.launchOrbit.altitude + 6378136.3, // earth radius
          eccentricity: 0.0,
          inclination: launch.launchOrbit.inclination,
          raanAtReferenceDate: launch.raanAtLaunch,
          referenceDate: launch.launchDate,
          targetDate: targetDate
        }
        let data = await this.$http
          .post('/physics/computeraan', raanCalculator)
          .then(function (response) {
            return response.data
          })
          .catch((e) => {})
        if (ltanOrRaan === 'raan') {
          return {
            label:
              'RAAN at reference date ' +
              dataTools.displayNdigits(dataTools.rad2deg(data.raan), 2) +
              ' deg',
            raan: data.raan
          }
        } else {
          return {
            label:
              'MLTAN at reference date ' +
              dataTools.displayNdigits(dataTools.sec2hour(data.ltan), 2) +
              ' h',
            raan: data.raan
          }
        }
      }
      return newRaan
    },

    addBooking() {
      if (this.inputs.bookings == null) {
        Vue.set(this.inputs, 'bookings', [])
      }
      let newBooking = {
        launch: null,
        numberOfSpacecraftsPerPlanes: []
      }
      for (let i = 1; i <= this.planesCount; i++) {
        newBooking.numberOfSpacecraftsPerPlanes.push(0)
      }
      this.inputs.bookings.push(newBooking)
    },

    deleteBooking(index) {
      this.inputs.bookings.splice(index, 1)
    },
    isExoInputMultiple(item) {
      return item === 'launches'
    }
  }
}
</script>
<style scoped>
.v-data-table {
  max-width: 100%;
}
</style>
